import { Brain, ChevronDown } from 'lucide-react';
import { memo, useEffect, useState } from 'react';
import { harden } from 'rehype-harden';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import { Streamdown } from 'streamdown';
import type { PluggableList } from 'unified';
import 'katex/dist/katex.min.css';

/**
 * 脚注パターンを検出する正規表現
 * - 脚注参照: [^1], [^abc] など
 * - 脚注定義: [^1]: テキスト など
 */
const FOOTNOTE_PATTERN = /\[\^[^\]\s]+\]/;

/**
 * カスタムremarkプラグイン設定
 * - remarkGfm: GFM（GitHub Flavored Markdown）対応（テーブル、打ち消し線、タスクリストなど）
 * - remarkMath: LaTeX数式構文（$...$, $$...$$）を認識
 * 注: Streamdownのデフォルトプラグインを上書きするため、明示的にremarkGfmを追加
 */
const customRemarkPlugins: PluggableList = [remarkGfm, remarkMath];

/**
 * カスタムrehypeプラグイン設定
 * rehype-hardenの設定を緩和して、ローカルIPアドレスへのリンクも許可
 */
const customRehypePlugins: PluggableList = [
  rehypeRaw,
  [rehypeKatex, { errorColor: '#ef4444' }],
  [
    harden,
    {
      defaultOrigin: typeof window !== 'undefined' ? window.location.origin : 'https://localhost',
      allowedImagePrefixes: ['https://', 'http://'],
      allowedLinkPrefixes: ['https://', 'http://', 'mailto:'],
      allowedProtocols: ['https:', 'http:', 'mailto:'],
      allowDataImages: false,
    },
  ],
];

/**
 * 外部リンクコンポーネント
 */
export const ExternalLink = ({
  href,
  children,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
  return (
    <a
      href={href}
      className="text-primary-400 hover:text-primary-300 underline"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    >
      {children}
    </a>
  );
};

/**
 * Markdownコンテンツコンポーネント（Streamdown使用）
 * LaTeX数式とMermaid図表のレンダリングに対応
 */
export const MarkdownContent = memo(
  ({ content }: { content: string }) => {
    const [renderKey, setRenderKey] = useState(0);
    const hasFootnotes = FOOTNOTE_PATTERN.test(content);

    // 脚注がある場合、内容が変わったら少し遅延して再レンダリングをトリガー
    useEffect(() => {
      if (hasFootnotes) {
        const timer = setTimeout(() => {
          setRenderKey((prev) => prev + 1);
        }, 100);
        return () => clearTimeout(timer);
      }
    }, [hasFootnotes]);

    if (!content) return null;

    return (
      <div className="markdown-content max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
        <Streamdown
          key={hasFootnotes ? `footnote-${renderKey}` : undefined}
          components={{
            a: ExternalLink,
          }}
          mode={hasFootnotes ? 'static' : 'streaming'}
          remarkPlugins={customRemarkPlugins}
          rehypePlugins={customRehypePlugins}
          mermaid={{ config: { theme: 'dark' } }}
        >
          {content}
        </Streamdown>
      </div>
    );
  },
  (prevProps, nextProps) => prevProps.content === nextProps.content,
);

MarkdownContent.displayName = 'MarkdownContent';

/**
 * 推論カードコンポーネント（desktop-appと同じ構造）
 */
export function ReasoningCard({ content }: { content: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="mb-4">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-muted-foreground text-sm cursor-pointer hover:text-foreground transition-colors"
      >
        <Brain className="w-4 h-4" />
        <span>推論プロセス</span>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : 'rotate-0'}`}
        />
      </button>
      {isOpen && (
        <div className="mt-4 text-sm">
          <div className="prose dark:prose-invert prose-sm max-w-none text-muted-foreground">
            <MarkdownContent content={content} />
          </div>
        </div>
      )}
    </div>
  );
}
